import type { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import type { ISettingsParams } from '../../components/BookingCalendar/settingsParams';
import type { IStyleParamsBoolean } from '../../components/BookingCalendar/stylesParams';
import type { CalendarContext } from '../context/contextFactory';

/**
 * Header filter visibility is stored twice — a legacy settings boolean and a
 * style param — and the two can disagree on a site where only one was ever
 * written. The components read the style param while `generateCustomCssVars`
 * resolves the settings boolean first, so a filter is off screen the moment
 * either source hides it.
 */
export const isHeaderFilterVisible = ({
  settingsValue,
  styleValue,
}: {
  /** `-2` until the setting is initialized, hence not a boolean. */
  settingsValue: unknown;
  styleValue?: boolean;
}): boolean => settingsValue !== false && styleValue !== false;

export type HeaderFilterVisibilityContext = Pick<
  CalendarContext,
  'settings' | 'settingsParams' | 'flowAPI' | 'isDateAndTimeViewMode'
>;

type HeaderVisibilityParam = keyof ISettingsParams & keyof IStyleParamsBoolean;

const isHeaderElementVisible = (
  { settings, settingsParams, flowAPI }: HeaderFilterVisibilityContext,
  param: HeaderVisibilityParam,
): boolean =>
  isHeaderFilterVisible({
    settingsValue: settings.get(settingsParams[param]),
    styleValue: getBooleanStyleParam(flowAPI, param),
  });

/**
 * The staff filter's gender chips are the only control that can clear a staff
 * gender, so anything acting on a gender has to know whether they are rendered.
 *
 * Three switches drop them independently: the staff filter's own pair, the
 * header's pair — `Header.st.css` gives the whole header `display: none`, the
 * filters inside it included — and the date-and-time view mode, which builds no
 * filter view models at all. The fourth, `headerFiltersVisibility`, needs no
 * check of its own: the per-filter params are declared as refs to it, so
 * `settings.get(headerStaffFilterVisibility)` already resolves to it wherever
 * the staff filter was never written on its own.
 */
export const isStaffFilterVisibleInHeader = (
  context: HeaderFilterVisibilityContext,
): boolean =>
  !context.isDateAndTimeViewMode &&
  isHeaderElementVisible(context, 'headerVisibility') &&
  isHeaderElementVisible(context, 'headerStaffFilterVisibility');

const getBooleanStyleParam = (
  flowAPI: ControllerFlowAPI,
  key: keyof IStyleParamsBoolean,
): boolean | undefined =>
  flowAPI.controllerConfig.config?.style?.styleParams?.booleans?.[key];
